import { Button, Label, TextArea, TextInput } from '@app/components';
import { DateTimePicker } from '@app/components/form/DateTimePicker';
import { Toggle } from '@app/components/form/Toggle';
import { OrderModeSelect } from '@app/components/form/selects/features/OrderModeSelect';
import { ShopSelect } from '@app/components/form/selects/features/ShopSelect';
import { StyledForms } from '@app/components/styled/form/Form';
import { ConnectorTypes, PromotionConditionType, ScheduleAvailabilityTypeEnum } from '@app/common';
import React, { forwardRef, Fragment, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ScheduleForm } from '@app/components/form/ScheduleForm';
import { InputFile, InputFileRef } from '@app/components/form/InputFile';
import { TagSelect } from '@app/components/form/selects/features/TagSelect';
import { nanoid } from '@reduxjs/toolkit';
import { PromotionConditionForm } from './PromotionConditionForm';
import { PromotionActionForm } from './PromotionActionForm';
import { StyledInputs } from '@app/components/styled/form/Inputs';
import { FormSection } from '@app/components/styled/form/FormSection';
import { NumberInput } from '@app/components/form/NumberInput';
import { FieldWithLabel, HelperText } from '@app/components/styled/form/Label';
import { ConnectorSelect } from '@app/components/form/selects/features/ConnectorSelect';
import { PromotionScopeSelect } from './PromotionScopeSelect';
import { View } from 'react-native';

export const PromotionForm = forwardRef<any>((_, ref: React.ForwardedRef<InputFileRef>) => {
  const { watch, formState: { errors } } = useFormContext();

  const [isPromotionCode, availabilityType] = watch(['isPromotionCode', 'availabilityType']);

  const [openAction, setOpenAction] = useState<Record<number, boolean>>({});
  const [openCondition, setOpenCondition] = useState<Record<number, boolean>>({});

  const { append: appendCondition, remove: removeCondition, update: updateCondition, fields: conditions } = useFieldArray({
    name: 'conditions',
  });

  const { append: appendAction, remove: removeAction, update: updateAction, fields: actions } = useFieldArray({
    name: 'actions',
  });


  const handleActionDropdownOpen = (index: number, open: boolean) => {
    setOpenAction((prev) => ({ ...prev, [index]: open }));
  }

  const handleConditionDropdownOpen = (index: number, open: boolean) => {
    setOpenCondition((prev) => ({ ...prev, [index]: open }));
  }

  const addCondition = () => {
    appendCondition({
      referenceId: `condition_${nanoid()}`,
      type: PromotionConditionType.ORDER_TOTAL,
      details: {},
    });
  }

  const addAction = () => {
    appendAction({
      referenceId: `action_${nanoid()}`,
      type: null,
      details: {},
    });
  }

  return (
    <StyledForms.Form>

      <InputFile name="image" ref={ref} />

      <FormSection.Section>
        <FormSection.Title>Informations générales</FormSection.Title>
        <FormSection.Content>
          <TextInput
            name="name"
            label="Nom"
            autoCapitalize="none" />

          <TextArea
            name="description"
            label="Description"
            autoCapitalize="none" />

          <FieldWithLabel label="Promotion à code">
            <Toggle
              name="isPromotionCode"
              options={[
                { title: "Oui", value: true },
                { title: "Non", value: false },
              ]} />
          </FieldWithLabel>

          {isPromotionCode ? (
            <Fragment>
              <TextInput
                name="code"
                label="Code"
                autoCapitalize="none" />

              <ConnectorSelect
                name="connectors"
                onBuildFilters={(search) => ({
                  $and: [
                    {
                      name: {
                        $contains: search
                      },
                    },
                    {
                      type: {
                        $notIn: [ConnectorTypes.HUBRISE, ConnectorTypes.PREVIEW]
                      },
                    },
                  ],
                })}
                label="Connecteurs"
                isMultiple />
            </Fragment>
          ) : (
            <Fragment>
              <TagSelect
                name="tags"
                isMultiple />

              <PromotionScopeSelect
                name="scopes"
                isMultiple />

              <HelperText>
                (WEB UNIQUEMENT) Les promotions peuvent être affchées dans une catégorie, dans le panier ou les deux.
              </HelperText>
            </Fragment>
          )}

          <NumberInput
            name="priority"
            label="Priorité"
            helper="La promotion valide avec la plus haute priorité sera affectée en premier"
            step={1} />

          <Toggle
            name="disabled"
            options={[
              { title: "Active", value: false },
              { title: "Inactive", value: true },
            ]} />
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Période de validité</FormSection.Title>
        <FormSection.Content>
          <DateTimePicker
            name="startDate"
            type="datetime-local"
            label="Date de début"
            helper="Laisser vide si la promotion n'a pas de limite de validité" />

          <DateTimePicker
            name="endDate"
            type="datetime-local"
            label="Date de fin"
            helper="Laisser vide si la promotion n'a pas de limite de validité" />

          <Toggle
            name="availabilityType"
            options={[
              { title: "Toujours disponible", value: ScheduleAvailabilityTypeEnum.always },
              { title: "Jours spécifiques", value: ScheduleAvailabilityTypeEnum.specificDays },
            ]} />

          {availabilityType === ScheduleAvailabilityTypeEnum.specificDays && (
            <ScheduleForm
              label="Horaires de disponibilité"
              names={{
                schedule: 'availabilitySchedule',
                selectedDays: 'selectedDays',
                startTime: 'startTime',
                endTime: 'endTime',
              }} />
          )}

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Paramètres de la promotion</FormSection.Title>
        <FormSection.Content>

          <OrderModeSelect
            name="orderModes"
            isMultiple />

          <ShopSelect
            label="Restaurants éligibles"
            name="shops"
            isMultiple />

          <HelperText>
            Laisser vide pour tous les restaurants
          </HelperText>

          <NumberInput
            name="maxUses"
            min={0}
            label="Nombre maximal d'utilisation"
            helper="Laisser vide si la promotion n'a pas de limite d'utilisation" />

          <FieldWithLabel label="Cumulable">
            <Toggle
              name="cumulative"
              helper="Peut se cumuler avec d’autres promotions"
              options={[
                { title: "Oui", value: true },
                { title: "Non", value: false },
              ]} />
          </FieldWithLabel>

          <FieldWithLabel label="Répétable">
            <Toggle
              name="repeatable"
              helper="Peut s’appliquer plusieurs fois dans une même commande"
              options={[
                { title: "Oui", value: true },
                { title: "Non", value: false },
              ]} />
          </FieldWithLabel>

          <FieldWithLabel label="Utilisation unique par client">
            <Toggle
              name="uniqueByCustomer"
              options={[
                { title: "Oui", value: true },
                { title: "Non", value: false },
              ]} />
          </FieldWithLabel>

        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Conditions</FormSection.Title>
        <FormSection.Content>
          {conditions.map((field, index) => (
            <PromotionConditionForm
              key={field.id}
              index={index}
              isOpen={openCondition[index]}
              setIsOpen={(isOpen) => handleConditionDropdownOpen(index, isOpen)}
              remove={removeCondition}
              update={updateCondition} />
          ))}

          <Button
            onPress={addCondition}
            title="Ajouter une condition"
            bordered
            grow
            large
            square
            smallText />
        </FormSection.Content>
      </FormSection.Section>

      <FormSection.Section>
        <FormSection.Title>Actions</FormSection.Title>
        <FormSection.Content>
          {actions.map((field, index) => (
            <PromotionActionForm
              key={field.id}
              index={index}
              isOpen={openAction[index]}
              setIsOpen={(isOpen) => handleActionDropdownOpen(index, isOpen)}
              update={updateAction}
              remove={removeAction} />
          ))}

          <StyledInputs.Holder>
            <Button
              onPress={addAction}
              title="Ajouter une action"
              bordered
              grow
              large
              square
              smallText />

            {!!errors.actions?.message && (
              <StyledForms.Error>
                <StyledForms.ErrorLabel>
                  {errors.actions.message.toString()}
                </StyledForms.ErrorLabel>
              </StyledForms.Error>
            )}
          </StyledInputs.Holder>
        </FormSection.Content>
      </FormSection.Section>

    </StyledForms.Form>
  );
});